/** The test sprint's tasks: /proves, the session page's "Proves" section, the ledger's evidence. */
export const ca_tests = {
  /** A tester's first two moves, on the first sign-in and on the home page. */
  testerStart: {
    title: 'Per començar a fer proves',
    intro: 'Dos passos, i ja pots fer la primera prova.',
    steamTitle: '1. Enllaça el teu Steam.',
    steamHereHint:
      'Aquí sota, a «Steam»: així el joc sap que les respostes i les captures que envies són teves.',
    steamHint: 'Així el joc sap que les respostes i les captures que envies són teves.',
    steamLink: 'Enllaça’l a Configuració',
    provesTitle: '2. Agafa una prova.',
    provesHint:
      'A «Proves», a la safata «Per fer», obre una prova oberta i clica «Ho faig»; la pàgina de la prova et diu on es fa i com.',
    provesLink: 'Obre «Proves»',
    done: 'fet',
  },
  testsPage: {
    title: 'Proves',
    intro:
      'Hi ha coses que la plataforma no pot veure sola i demana a una persona: una prova guiada dins del joc, una màquina amb el joc obert una estona, o una pregunta amb una imatge. Cada prova és curta, diu on es fa i com, i es contesta des del joc o aquí mateix. Qualsevol membre pot agafar-ne una quan li vagi bé.',
    how: {
      title: 'Com funciona',
      steps: [
        {
          title: 'Tria una prova',
          text: 'Obre l’enllaç de l’anunci de Discord (o el que t’han passat) i clica «Ho faig»: la prova queda al teu nom i la trobaràs aquí, a «Proves».',
        },
        {
          title: 'Prepara’t',
          text: 'Cada prova diu on es fa i què cal. Per a una prova dins del joc: el watcher obert (el programa de la plataforma per al teu PC) i entrar a la partida que indica.',
        },
        {
          title: 'Dins del joc',
          text: 'Els passos surten a la pantalla d’un en un: F10 sí, F11 no, F8 no ho veig. F9 obre una caixa per escriure què veus, i Enter ho envia amb un informe del que tens davant.',
        },
        {
          title: 'En acabar',
          text: 'L’últim pas tanca la prova sol. Si has vist res que els passos no preguntaven, escriu-ho a la pàgina de la prova.',
        },
      ],
    },
    open: 'Obertes',
    openHint: 'Clica una prova per veure on es fa i com.',
    none: 'Ara mateix no hi ha cap prova per fer.',
    history: 'Historial',
    noHistory: 'Encara no s’ha fet cap prova.',
    captures: 'Captures',
    capturesHint:
      'Les captures del watcher d’una partida són a la pàgina de la sessió, a la secció «Proves». Les de les campanyes d’inspecció, amb la revisió de cada foto, són a:',
    inspections: 'Inspeccions',
    kinds: {
      play: 'Joc',
      lend: 'Deixa la màquina',
      judge: 'Una pregunta',
    } as Record<string, string>,
    // The admin's form: one «play» kind that is then split into game and web.
    createKinds: {
      play: 'Prova de joc o de web',
      lend: 'Deixa la màquina',
      judge: 'Una pregunta',
    } as Record<string, string>,
    kindHint: {
      play: 'Entres a la sessió, segueixes els passos a la pantalla i contestes amb F10 (sí), F11 (no) o F8 (no ho veig). F9 obre una caixa per explicar què veus.',
      lend: 'Deixes el watcher obert amb «es pot deixar» marcat; la plataforma obre el joc, mira el que ha de mirar i el tanca. No has de fer res més.',
      judge: 'Mires la pregunta (i la imatge, si en porta) i tries una resposta.',
    } as Record<string, string>,
    states: {
      open: 'oberta',
      claimed: 'agafada',
      done: 'contestada',
      closed: 'tancada',
      cancelled: 'anul·lada',
    } as Record<string, string>,
    minutes: (n: number) => `${n} min`,
    needed: (n: number) => `${n} persones`,
    track: 'Pista',
    session: 'Sessió',
    claim: 'Ho faig',
    claimHint: 'Queda al teu nom; els passos t’esperen quan entris al joc.',
    claimHints: {
      play: 'Queda al teu nom; els passos t’esperen quan entris al joc.',
      lend: 'Queda al teu nom; quan la partida estigui en marxa, la plataforma obrirà el joc a la teva màquina.',
      judge: 'Queda al teu nom i llavors podràs triar la resposta.',
    } as Record<string, string>,
    claimFirst: 'Clica «Ho faig» a dalt i podràs contestar.',
    loading: 'Carregant la prova…',
    steamMissing:
      'El teu compte no té Steam enllaçat: el joc no sabrà que les respostes i les captures són teves. Fes-ho abans d’entrar.',
    steamLink: 'Enllaça’l a Configuració',
    forYou: 'Per a tu',
    forYouIntro: 'El que necessites per fer la prova, en ordre.',
    where: 'On',
    whereNone: 'Aquesta prova no necessita entrar a cap partida.',
    whereLend:
      'No cal que hi entris tu: la plataforma obre el joc a la teva màquina. Només cal que tinguis instal·lats els mods de la partida.',
    sessionPage: 'Pàgina de la partida',
    howTo: 'Com',
    howToSteps: {
      play: [
        'Obre el watcher abans que el joc i deixa’l obert: puja per tu les captures i el registre del joc.',
        'Entra a la partida amb l’adreça i la contrasenya de dalt, i tria un lloc.',
        'Els passos surten a la pantalla d’un en un. Contesta cada pas amb F10 (sí), F11 (no) o F8 (no ho veig). F9 obre una caixa per escriure què veus; Enter ho envia.',
      ],
      lend: [
        'Obre el watcher i marca-hi «es pot deixar». No cal que obris el joc.',
        'La plataforma obre el joc a la teva màquina, mira el que ha de mirar i el tanca. Mentre ho fa, no toquis res.',
      ],
      judge: ['Llegeix la pregunta, mira la imatge si en porta i tria la resposta que et sembli.'],
    } as Record<string, string[]>,
    lendStatus: {
      title: 'La teva màquina',
      waiting:
        'Esperant que la missió de la sessió estigui en marxa. Llavors el joc s’obrirà sol a la teva màquina.',
      pending: 'La plataforma ha demanat al teu watcher que obri el joc.',
      taken: 'El teu watcher ha rebut l’encàrrec i obre el joc.',
      done: 'El joc és obert a la teva màquina. No cal que hi toquis res.',
      failed: 'El watcher no ha pogut obrir el joc:',
      cancelled: 'L’encàrrec s’ha retirat:',
      released: 'La prova s’ha acabat: la plataforma tanca el joc que havia obert.',
      releasedIdle: 'La prova s’ha acabat i la teva màquina queda lliure.',
      notPresent: 'El teu watcher no dona senyals de vida: obre’l en aquesta màquina.',
      notLendable: 'Al watcher, marca «es pot deixar»: fins llavors el joc no s’obrirà.',
    },
    yourSteps: 'Els passos, i el que ha arribat del joc',
    keys: 'F10 sí · F11 no · F8 no ho veig · F9 escriu',
    keycaps: [
      ['F10', 'sí'],
      ['F11', 'no'],
      ['F8', 'no ho veig'],
      ['F9', 'escriu-ho'],
    ] as [string, string][],
    keycapsHint:
      'Les tecles, dins del joc. El pas que toca surt a dalt a l’esquerra de la pantalla.',
    nextStep: 'Següent',
    progress: (done: number, total: number) => `${done} de ${total} passos`,
    phases: {
      watcher: 'El watcher',
      join: 'Entrar',
      base: 'La base',
      map: 'El mapa',
      radio: 'La ràdio',
      transport: 'El transport',
      return: 'La tornada',
      objective: 'L’objectiu',
      fight: 'El combat',
      look: 'Mirar',
    } as Record<string, string>,
    stages: {
      play: [
        'Agafa-la',
        'Entra al servidor',
        'Juga i contesta',
        'Explica què has vist',
        'Revisada',
      ],
      lend: ['Agafa-la', 'Deixa el watcher obert', 'La plataforma hi juga', 'Revisada'],
      judge: ['Mira-ho', 'Tria', 'Revisada'],
    } as Record<string, string[]>,
    stagesLabel: 'On ets',
    kindShort: {
      play: 'Jugues tu',
      lend: 'Juga la plataforma',
      judge: 'Només tries',
    } as Record<string, string>,
    trackNames: {
      land: 'terra',
      sea: 'mar',
      ww2: 'Segona Guerra Mundial',
    } as Record<string, string>,
    autoDone: 'L’últim pas tanca la prova sol.',
    whatSeen: 'Què has vist?',
    whatSeenHint:
      'Tot el que els passos no preguntaven: on, què, i què esperaves. Una captura també hi va bé.',
    followUp: 'Seguiment',
    followUpIntro:
      'Aquesta part l’omple qui revisa la prova, no tu: el veredicte i què ha canviat gràcies a la prova.',
    timelineLink: 'Línia de temps de la sessió',
    pending: 'Encara sense revisar.',
    adminTools: 'Administració: crear una prova a mà',
    adminToolsHint:
      'Normalment les crea l’agent per l’API i s’anuncien a Discord. Aquest formulari és per fer-ne una a mà.',
    claimedBy: (who: string) => `Agafada per ${who}`,
    mine: 'És teva',
    steps: 'Passos',
    stepExpect: 'Hauries de veure:',
    stepGoodNo: 'Aquí la resposta bona és «no».',
    // What the platform's reading of a step's wording warns an admin about (the
    // owner, 2026-10-12): a step is one question a key answers with no doubt.
    stepWarningsTitle: 'Redacció del pas',
    stepWarnings: {
      no_question:
        'No acaba amb un interrogant: ha de ser una pregunta que es contesti amb sí o no.',
      several_questions: 'Hi ha més d’una pregunta: una sola per pas.',
      conditional:
        'Hi ha una condició («si n’hi ha», «si és…»): pregunta només pel que aquesta missió té.',
      joined_facts: 'Ajunta més d’un fet: un pas per a cada cosa que s’ha de veure.',
      negative_opening:
        'La pregunta comença en negatiu: pregunta-ho en positiu, de manera que «sí» vulgui dir que ha anat bé.',
      too_long: 'És massa llarg per llegir-lo al joc: més de 220 caràcters.',
      not_in_mission:
        'Pregunta pel generador, i el campament d’una missió de la Segona Guerra Mundial no en té: treu el pas.',
    },
    stepRemove: 'Treu el pas',
    stepAdd: 'Afegeix un pas',
    stepAddHint:
      'Mentre ningú hagi agafat la prova. El mateix format: id | què fer i la pregunta | què s’hauria de veure | fila del registre | resposta bona. Sense id va al final amb un de nou.',
    stepAddAfter: 'Després del pas',
    stepAddLast: 'al final',
    createStepsRules:
      'Cada pas és una sola pregunta que es contesta amb sí o no: un sol fet, sense «si n’hi ha», en positiu («sí» vol dir que ha anat bé). Si cal fer alguna cosa, primer la instrucció i després la pregunta. Un cop creada, la fitxa avisa dels passos que no ho compleixen.',
    answers: 'Respostes rebudes del joc',
    noAnswers: 'Encara no ha arribat cap resposta del joc.',
    answerYes: 'sí',
    answerNo: 'no',
    answerCant: 'no ho veig',
    report: 'amb informe',
    // The tester's own correction of a step, on the page (the owner, 2026-09-25).
    fixLabel: 'La teva resposta a aquest pas',
    fixYes: 'Sí',
    fixNo: 'No',
    fixCant: 'No ho veig',
    fixHint:
      'Tothom qui ha jugat la prova hi contesta, des del joc o des d’aquí. La teva resposta és teva: no trepitja la de ningú, i la del joc es guarda igualment.',
    autosaveHint: 'Tot el que marques i escrius es desa sol, i els altres ho veuen mentre ho fas.',
    saving: 'Desant…',
    saved: 'Desat',
    saveError: 'No s’ha pogut desar; es torna a provar quan escriguis.',
    othersNotes: 'El que hi han dit els altres',
    gameReports: 'Dit des del joc amb F9',
    gameReportsHint:
      'Els comentaris que cadascú ha enviat mentre jugava, amb la captura del moment.',
    reportAt: (minutes: number) => `minut ${minutes}`,
    reportPicture: (who: string) => `Captura de ${who}`,
    reportNoNote: 'sense text',
    noPicture: 'sense captura',
    edited: (game: string) => `(corregit; el joc va dir «${game}»)`,
    editedNoGame: '(contestat des de la web)',
    stepNote: 'Comentari sobre aquest pas',
    stepNotePlaceholder: 'Què has vist exactament en aquest pas?',
    saveStepNote: 'Desa',
    question: 'Pregunta',
    answerQuestion: 'Contesta',
    done: 'Ja he acabat',
    doneHint: 'Si t’has quedat a mitges, tanca-la aquí; el que hagis contestat al joc ja hi és.',
    doneStopsHint: 'Tanca la prova i atura el servidor. El que hagis contestat al joc ja hi és.',
    noteLabel: 'El teu comentari: tot el que els passos no preguntaven',
    notePlaceholder: 'Dues línies: què has vist, on, i què esperaves.',
    addNote: 'Afegeix',
    dropFile: 'Puja una captura o un fitxer',
    uploadsOff:
      'Les pujades no estan activades en aquesta instal·lació; escriu-ho al teu comentari.',
    entries: 'El que s’hi ha afegit',
    file: 'fitxer',
    result: 'Resultat',
    option: (o: string) => `Resposta: ${o}`,
    closedNote: 'Què ha canviat',
    verdict: 'Veredicte',
    close: 'Tanca la prova',
    closeNote: 'Una frase: què ha canviat gràcies a aquesta prova.',
    cancel: 'Anul·la',
    deleteTitle: 'Elimina la prova',
    deleteHint:
      'Una prova anul·lada es pot eliminar del tot: desapareix de les llistes amb les respostes, els comentaris i els fitxers. La partida que va engegar queda al registre de sessions.',
    deleteButton: 'Elimina-la definitivament',
    deleteConfirm: (title: string) =>
      `S’eliminarà «${title}» amb les respostes, els comentaris i els fitxers, i no es podrà recuperar. Continuem?`,
    create: 'Nova prova',
    createTitle: 'Títol',
    createBrief: 'Explicació',
    createTrack: 'Pista (terra, mar, ww2)',
    createSession: 'Codi de la sessió (per a una prova guiada)',
    createDuration: 'Minuts',
    createNeeded: 'Persones',
    createSteps:
      'Passos, un per línia: id | què fer | què s’hauria de veure | fila del registre | resposta bona (sí o no; sí si no hi és)',
    createQuestion: 'Pregunta',
    createOptions: 'Respostes possibles, una per línia',
    createPerformer: 'Qui la juga',
    createPerformerHint:
      'Una prova que juga Claude no s’anuncia a Discord ni s’ofereix a ningú, i les seves respostes no compten com a evidència d’una persona.',
    performerChange: 'Canvia qui l’ha jugada',
    createSubmit: 'Crea i anuncia',
    announced: 'Anunciada a Discord',
    notAnnounced: 'Sense anunci a Discord (canal no configurat)',
    back: 'Totes les proves',
    /** A web card: its steps are done in the browser, and nothing of the game is on its page. */
    web: {
      chip: 'Web',
      short: 'Es fa aquí, a la web, sense obrir el joc.',
      claim: 'Comença-la',
      claimHint: 'Queda al teu nom i la portes a sobre a totes les pàgines fins que l’acabis.',
      stages: ['Comença-la', 'Fes els passos', 'Contesta', 'Dona-la per acabada', 'Revisada'],
      howTo: [
        'Prem «Obre» a cada pas: et porta a la pàgina on comença.',
        'Mira el que el pas diu i contesta sí, no o no ho puc dir.',
        'Escriu en una línia el que hagis vist diferent.',
      ],
      steps: 'Els passos',
      stepsHint:
        'Cada resposta es desa sola. Mentre la tinguis agafada, el tauler «Prova en curs» et segueix a totes les pàgines.',
      stepOf: (n: number, total: number) => `Pas ${n} de ${total}`,
      open: 'Obre',
      openTitle: (url: string) => `Obre ${url}`,
      forRole: (role: string) => `Per a: ${role}`,
      finish: 'Dona-la per acabada',
      finishHint: 'Totes les respostes ja són desades; amb això la prova passa a revisió.',
      finishMissing: (n: number) =>
        n === 1 ? 'Et falta contestar 1 pas.' : `Et falten ${n} passos per contestar.`,
      draft: 'Esborrany',
      draftHint: 'Ningú la veu ni s’ha anunciat. Repassa els passos i obre-la quan estigui bé.',
      openDraft: 'Obre-la',
      audienceAdmins: 'Només administradors',
      roadmapItem: (id: string) => `Punt del full de ruta: ${id}`,
      olderVersion: (v: string) => `Creada a la versió ${v}, anterior a l’actual.`,
      context: (path: string, size: string, version: string) =>
        [path, size, version].filter(Boolean).join(' · '),
      createMedium: 'On es fa',
      mediums: { game: 'Joc', web: 'Web' } as Record<string, string>,
      createAudience: 'A qui s’ofereix',
      audiences: { testers: 'Beta testers', admins: 'Només administradors' } as Record<
        string,
        string
      >,
      createRoadmapItem: 'Punt del full de ruta que comprova (el seu identificador)',
      createDraft: 'Crea-la com a esborrany, sense anunciar-la',
      createStepsUrl:
        'En una prova web, afegeix al final de cada pas la pàgina on comença: … | resposta bona | /library. Ha de ser un camí d’aquesta web, començat per «/».',
      importTitle: 'Importa un lot de fitxes',
      importHint:
        'Un fitxer JSON amb una llista de fitxes. Es creen totes com a esborrany: ningú les veu ni s’anuncien fins que les obres.',
      importFile: 'Fitxer del lot',
      importDone: (created: number, skipped: number) =>
        `${created === 1 ? '1 fitxa creada' : `${created} fitxes creades`} com a esborrany` +
        (skipped > 0
          ? `; ${skipped === 1 ? '1 ja hi era' : `${skipped} ja hi eren`} i no s’ha tornat a crear.`
          : '.'),
      importBad: 'El fitxer no és una llista de fitxes en JSON.',
    },
    joinHint: (code: string) => `Els detalls per entrar són a la pàgina de la sessió ${code}.`,
    // A "test card" (a play task with a mission and a server) starts its own
    // session with one click, and, once it runs, joins it with the watcher.
    serverBusy: (code: string) => `El servidor ja té una altra partida en marxa: ${code}.`,
    // Whose it is, so the refusal can be acted on: the card that session belongs
    // to, and, for whoever may end it, the button that does and starts this one.
    serverBusyCard: (code: string, title: string, state: string) =>
      `El servidor encara té en marxa la partida ${code}, de la prova «${title}»${state ? ` (${state})` : ''}.`,
    serverBusyOpenCard: 'Obre aquella prova',
    serverBusyEnd: 'Acaba aquella partida i engega aquesta',
    serverBusyConfirm: (code: string) =>
      `S’acabarà la partida ${code} per a tothom qui hi sigui. Vols continuar?`,
    serverBusyAsk:
      'La pot acabar qui tenia aquella prova o un administrador; després torna a engegar el servidor.',
    // The card's server, in one box (the owner, 2026-10-02): off, starting, up,
    // and that finishing the test stops it.
    server: {
      title: 'El servidor de la prova',
      off: 'El servidor és aturat',
      offHint:
        'Engega’l quan vulgueu jugar. Triga uns minuts a carregar els mods i la missió, i s’atura sol quan acabes la prova.',
      start: 'Engega el servidor',
      restart: 'Torna a engegar el servidor',
      requesting: 'Engegant…',
      claimFirst: 'Agafa la prova (a dalt) i podràs engegar el servidor.',
      notYours: (who: string | null) =>
        `El servidor l’engega qui ha agafat la prova${who ? ` (${who})` : ''}. Quan sigui en marxa, aquí sortirà com entrar-hi.`,
      waiting: 'La partida ja està creada',
      waitingHint: 'El servidor encara no s’ha engegat: ho fa qui dirigeix la partida.',
      starting: 'El servidor s’està engegant',
      startingHint:
        'Carrega els mods i la missió: són uns minuts. No cal que facis res, aquesta pàgina t’avisa quan s’hi pugui entrar. Mentrestant, obre el watcher i prepara els mods.',
      elapsed: (minutes: number) =>
        minutes < 1 ? 'fa menys d’un minut' : minutes === 1 ? 'fa 1 minut' : `fa ${minutes} minuts`,
      slow: 'Triga més del normal. Si no arrenca, avisa qui organitza la prova.',
      ready: 'El servidor és en marxa: ja hi pots entrar',
      readyHint: 'Entra-hi amb el botó o amb l’adreça de sota.',
      stopsOnDone: 'Quan cliquis «Ja he acabat», la partida es tanca i el servidor s’atura.',
      stopsWhenEmpty:
        'La prova ja és acabada: el servidor s’aturarà sol cinc minuts després que en surti l’últim jugador.',
      // «Atura el servidor» (the owner, 2026-10-15): whoever took the card, its
      // author or an admin stops the card's own server by hand, whatever the card's state.
      stop: 'Atura el servidor',
      stopping: 'Aturant…',
      stopHint:
        'La partida es tanca per a tothom i el servidor s’atura. La prova es queda com està.',
      confirmStopInGame:
        'Encara hi ha algú al joc: el seu joc ha donat senyals fa menys d’un minut i mig. Si atures el servidor, la partida es tancarà per a tothom qui hi sigui. Vols aturar-lo igualment?',
      ended: 'La partida s’ha acabat i el servidor s’ha aturat',
      endedHint: 'Si hi voleu tornar, es pot engegar de nou.',
      endedDone: 'No cal fer res més amb el servidor.',
      noMission: 'Aquesta prova encara no té cap partida',
      noMissionHint:
        'Qui organitza la prova li ha de posar la missió; quan la tingui, aquí sortirà el botó per engegar el servidor.',
      noMissionAdmin:
        'Sense missió no es pot engegar cap servidor. Escriu el codi de la missió (el de la seva adreça): es publicarà al servidor de la prova quan algú l’engegui.',
      missionCode: 'Codi de la missió',
      assign: 'Assigna la missió',
      confirmDone:
        'La partida es tancarà i el servidor s’aturarà per a tothom qui hi sigui. Vols acabar la prova?',
    },
    myConnect: {
      button: 'Obre el joc i entra',
      needsWatcher: 'Cal el watcher obert en aquest PC',
      download: 'Baixa el watcher',
      pending: 'Esperant que el teu watcher reculli l’encàrrec.',
      taken: 'El teu watcher ha rebut l’encàrrec i obre el joc.',
      done: 'El joc és obert. Entra-hi.',
      failed: 'No s’ha pogut obrir el joc:',
      cancelled: 'L’encàrrec s’ha retirat:',
    },
  },
  /** The one list of /proves: every test and every review, by tray. */
  inbox: {
    title: 'Proves i revisions',
    mineTitle: 'Les teves proves',
    mineIntro:
      'Les proves que has agafat. Les noves s’anuncien al canal de proves de Discord: obre l’enllaç de l’anunci, clica «Ho faig» i la trobaràs aquí.',
    mineNone: 'Encara no has agafat cap prova.',
    testerIntro:
      'A «Per fer» hi tens les proves obertes i les que has agafat: obre’n una i clica «Ho faig». A les altres safates, per llegir, el que ja està revisat i el que no cal revisar.',
    trays: {
      todo: 'Per fer',
      review: 'Per revisar',
      done: 'Revisades',
      skip: 'No cal revisar',
    } as Record<string, string>,
    trayHints: {
      todo: 'Proves que algú pot agafar o que ja s’estan fent.',
      todoMine: 'Les teves proves que encara s’estan fent.',
      todoTester: 'Les proves obertes que pots agafar, i les teves que encara s’estan fent.',
      review: 'Tot el que ja té resultats i espera que algú el valori.',
      done: 'El que ja té veredicte.',
      skip: 'El que no cal mirar, amb el motiu: substituït, antic, buit, automàtic o anul·lat.',
    } as Record<string, string>,
    empty: {
      todo: 'Ara mateix no hi ha cap prova per fer.',
      review: 'No hi ha res pendent de revisar.',
      done: 'Encara no s’ha revisat res.',
      skip: 'Res a mostrar aquí.',
    } as Record<string, string>,
    types: {
      all: 'Tots els tipus',
      play: 'Joc',
      lend: 'Deixa la màquina',
      judge: 'Pregunta',
      campaign: 'Gira d’inspecció',
      captures: 'Captures soltes',
      smoke: 'Nit de fum',
      web: 'Web',
    } as Record<string, string>,
    typeFilter: 'Tipus',
    openAllDrafts: (n: number) => (n === 1 ? 'Obre l’esborrany' : `Obre-les totes (${n})`),
    openAllConfirm: (n: number) =>
      n === 1
        ? 'La fitxa s’oferirà i s’anunciarà. Vols obrir-la?'
        : `Les ${n} fitxes s’oferiran i s’anunciaran. Vols obrir-les totes?`,
    search: 'Cerca',
    searchPlaceholder: 'títol, sessió, versió…',
    campaignTitlePlain: (what: string | null, tours: number) =>
      what ? `Gira d’inspecció · ${what}` : `Gira d’inspecció · ${tours} gires`,
    campaignTitle: (id: string, what: string | null, tours: number) =>
      what ? `Campanya ${id} · ${what}` : `Campanya ${id} · ${tours} gires`,
    capturesTitle: (session: string | null) =>
      session ? `Captures soltes · sessió ${session}` : 'Captures sense sessió',
    smokeTitle: (id: string, what: string | null) =>
      what ? `Nit de fum ${id} · ${what}` : `Nit de fum ${id}`,
    tours: (n: number, toReview: number) =>
      n === 1
        ? '1 gira'
        : toReview > 0 && toReview < n
          ? `${n} gires, ${toReview} per revisar`
          : `${n} gires`,
    pictures: (n: number) => (n === 1 ? '1 captura' : `${n} captures`),
    reviewed: (done: number, total: number) => `${done} de ${total} revisades`,
    steps: (done: number, total: number) => `${done} de ${total} passos contestats`,
    noAnswers: (n: number) => (n === 1 ? '1 «no»' : `${n} «no»`),
    problems: (n: number) => (n === 1 ? '1 amb problema' : `${n} amb problema`),
    session: (code: string) => `sessió ${code}`,
    version: (v: string) => (v === 'dev' ? 'versió de desenvolupament' : `v${v.replace(/^v/, '')}`),
    by: (who: string) => `la fa ${who}`,
    states: {
      open: 'Oberta',
      claimed: 'Agafada',
      answered: 'Per tancar',
      done: 'Per tancar',
      closed: 'Tancada',
      cancelled: 'Anul·lada',
      pending: 'Pendent',
      in_progress: 'En curs',
      reviewed: 'Revisada',
      superseded: 'Obsoleta',
      old: 'Antiga',
      no_captures: 'Sense captures',
      withheld: 'Retinguda',
      dismissed: 'Descartada',
      session_ended: 'Sessió acabada',
      draft: 'Esborrany',
      green: 'Verda',
      red: 'Vermella',
      aborted: 'Aturada',
      running: 'En marxa',
    } as Record<string, string>,
    pending: (n: number) => `Pendent ${n}`,
    inProgress: (n: number) => `En curs · en falten ${n}`,
    reasons: {
      superseded: (by: number | null) =>
        by ? `substituïda per la campanya ${by}` : 'substituïda per una gira més nova',
      old: 'fa més de catorze dies que ningú la mira',
      no_captures: 'la gira no va fer cap captura',
      withheld: 'totes les captures estan retingudes fins que un administrador les deixi veure',
      dismissed: 'descartada a mà',
      cancelled: 'anul·lada',
      session_ended: 'la sessió es va acabar sense cap resposta',
      automatic: 'la plataforma la valora sola; el detall és a «Estat»',
      reopened: 'demanada tot i estar substituïda',
      older_version: 'versió anterior: es va crear abans de la versió actual, i encara es pot fer',
    },
    actions: {
      review: 'Revisa',
      resume: 'Continua',
      open: 'Obre',
      take: 'Ho faig',
      answer: 'Contesta',
      reviewAnyway: 'Revisa igualment',
    },
    performers: {
      person: 'La fa una persona',
      agent: 'La fa Claude',
    } as Record<string, string>,
    performerShort: {
      person: 'persona',
      agent: 'Claude',
    } as Record<string, string>,
    select: 'Tria',
    selected: (n: number) => (n === 1 ? '1 prova triada' : `${n} proves triades`),
    markAgent: 'Les ha jugat Claude',
    markPerson: 'Les ha jugat una persona',
    cancelSelect: 'Deixa de triar',
    help: 'Com funciona una prova',
    machine: 'La meva màquina: el watcher',
    adminLinks: 'Totes les gires i el resum',
    allTours: 'Llista de totes les gires',
    summary: 'Resum de les inspeccions',
  },
  /** The session page's one line about its tests, and the admin's own technical page. */
  sessionTestsCard: {
    title: 'Proves d’aquesta sessió',
    pictures: (n: number, reviewed: number) =>
      n === 1
        ? `1 captura, ${reviewed === 1 ? 'revisada' : 'sense revisar'}.`
        : `${n} captures, ${reviewed} de revisades.`,
    review: 'Revisa-les a Proves',
    yourTest: 'La teva prova en aquesta sessió',
    technical: 'Dades tècniques de la sessió',
  },
  sessionTechnical: {
    title: (code: string) => `Dades tècniques · sessió ${code}`,
    intro:
      'El que la plataforma sap d’aquesta sessió per dins: on ha quedat cada peça i tot el que hi ha passat, en ordre. Només per a administradors.',
    back: 'Torna a la sessió',
    adminOnly: 'Aquesta pàgina és només per a administradors.',
    noTimeline: 'La plataforma no té cap línia de temps d’aquesta sessió.',
  },
  sessionTests: {
    title: 'Proves',
    none: 'Cap prova en aquesta sessió.',
    timeline: 'Línia de temps',
    sources: 'Fonts',
    sourceNames: {
      runtime: 'runtime',
      log: 'registre del servidor',
      watcher: 'watcher',
      tasks: 'proves',
    } as Record<string, string>,
    sourceUnits: {
      none: 'cap',
      events: ['esdeveniment', 'esdeveniments'],
      lines: ['línia', 'línies'],
      uploads: ['pujada', 'pujades'],
      tasks: ['prova', 'proves'],
      items: ['element', 'elements'],
    } as Record<string, string | [string, string]> & { none: string },
    showAll: 'Mostra-ho tot',
    showTests: 'Només les proves',
    capture: 'Captura del watcher (obre-la a mida real)',
  },
  watcherCard: {
    title: 'El watcher',
    intro:
      'Un programa petit per a la teva màquina: diu que hi és, obre i tanca el joc quan la plataforma li ho demana (només si has marcat que es pot deixar), puja les captures i les línies importants del registre del joc, i amb Ctrl+F9 fa una captura del joc i la puja (dins d’una missió, F9 obre una caixa per escriure i envia la captura amb el text). Només captura la finestra del joc, i només quan és al davant i res no la tapa: mai l’escriptori ni cap altra finestra; si no pot, no captura res i ho diu. Tot surt de la teva màquina; res hi entra.',
    notPublished:
      'La baixada del watcher encara no està disponible; demana-la a qui organitza les proves.',
    download: (v: string) => `Baixa el watcher${v ? ` v${v}` : ''}`,
    regenerate: 'Nova clau',
    tokenSet:
      'La baixada ja porta la teva clau personal; una clau nova deixa sense servei el watcher que ja tens.',
    tokenUnset: 'Baixar-lo és tota la configuració: el fitxer ja sap qui ets.',
    lendable: 'Màquines que es poden deixar ara',
    nobody: 'Ningú ara mateix.',
    gameOpen: 'joc obert',
    gameClosed: 'joc tancat',
    hotkey: 'tecla global',
    /** Whether the watcher holds the PC awake and unlocked; nothing when an older watcher did not say. */
    awake: (held: boolean | null | undefined) =>
      held == null ? '' : held ? ' · desperta' : ' · es pot adormir',
    locked: 'bloquejada',
    /** The DLCs the machine's Steam account owns, as its watcher reported them; null when it never said. */
    dlcs: (keys: string[] | null | undefined) =>
      keys == null
        ? 'DLC: no ho ha dit'
        : keys.length === 0
          ? 'sense DLC'
          : `DLC: ${keys.join(', ')}`,
    connect: 'Obre el joc a la sessió',
    disconnect: 'Tanca el joc',
    sessionForJob: 'Codi de la sessió on obrir el joc',
    queued: (kind: string, id: number) =>
      `Feina ${id} (${kind === 'connect' ? 'obrir' : 'tancar'}) encuada; el watcher la pren al proper batec.`,
    /** On a card's own page: why this test wants it, and how to have it running. */
    forCard: {
      play: 'Per a aquesta prova et cal el watcher a la màquina on jugues: puja per tu les captures i les línies importants del registre del joc, i pot obrir el joc a la partida.',
      lend: 'Per deixar la màquina et cal el watcher: és ell qui obre i tanca el joc quan la plataforma li ho demana.',
    } as Record<string, string>,
    steps: [
      'Baixa’l amb el botó, amb la sessió iniciada: el fitxer ja porta la teva clau personal i no cal escriure-hi res.',
      'Obre el fitxer que has baixat (és un programa de Windows). Si Windows avisa que no el coneix, tria «Més informació» i «Executa igualment».',
      'Deixa’l obert, abans d’obrir el joc. S’actualitza sol quan la plataforma en publica un de nou.',
    ],
    present: 'El teu watcher és obert i la plataforma el veu.',
    absent: 'Ara mateix la plataforma no veu el teu watcher.',
  },
  /** «Prova en curs»: the panel the shell draws on every page while a web card is taken and not finished. */
  testPanel: {
    title: 'Prova en curs',
    expand: 'Obre el tauler de la prova en curs',
    collapse: 'Plega',
    stepOf: (n: number, total: number) => `pas ${n} de ${total}`,
    open: 'Obre',
    answers: 'La teva resposta',
    yes: 'Sí',
    no: 'No',
    cant: 'No ho puc dir',
    note: 'Nota',
    notePlaceholder: 'Què has vist? (una línia)',
    previous: 'Anterior',
    next: 'Següent',
    card: 'Obre la fitxa',
    allAnswered: 'Tots els passos tenen resposta.',
    finish: 'Acaba la prova',
    finishing: 'Acabant…',
    finished: 'Prova acabada. Gràcies!',
    finishFailed: (why: string) => `No s’ha pogut acabar la prova: ${why}`,
    dismiss: 'D’acord',
    switcher: 'Quina prova',
    saving: 'Desant…',
    saved: 'Desat',
    error: 'No s’ha pogut desar. Torna-ho a provar.',
  },
  ledgerEvidence: {
    label: (yes: number, no: number, cant: number) => `${yes} sí · ${no} no · ${cant} no ho veig`,
    title: 'Evidència de les proves guiades: respostes per tecla, amb la sessió darrere',
  },
};
